import { ComponentType } from '@angular/cdk/overlay';
import { Injectable } from '@angular/core';
import {
    MatLegacySnackBar as MatSnackBar,
    MatLegacySnackBarConfig as MatSnackBarConfig,
    MatLegacySnackBarRef as MatSnackBarRef,
    LegacySimpleSnackBar as SimpleSnackBar
} from '@angular/material/legacy-snack-bar';
import { AuthErrors } from '../enums/auth-errors.enum';

const DEFAULT_MESSAGE_DURATION = 2000;

@Injectable({
    providedIn: 'root'
})
export class ToasterService {
    constructor(private snackBar: MatSnackBar) {}

    isOAuthError(error: string): boolean {
        return (
            error === AuthErrors.LoginRequired ||
            error === AuthErrors.InvalidGrant ||
            error === AuthErrors.MissingRefreshToken
        );
    }

    toast(message: string, msgDuration?: number): void {
        if (this.isOAuthError(message)) {
            return;
        }
        this.snackBar.open(message, '✖', {
            //unicode x2716
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: msgDuration && msgDuration > 0 ? msgDuration : DEFAULT_MESSAGE_DURATION,
            panelClass: 'nova-toast'
        });
    }

    /**
     * Display a toast message using a custom component.
     *
     * @param component A component to display as a toast message.
     * @param options Options to apply to the toast. These will override any default options.
     * @param msgDuration Duration to display the toast in milliseconds. (Default 2000ms)
     */
    toastComponent<T = unknown>(component: ComponentType<T>, options?: MatSnackBarConfig, msgDuration?: number) {
        this.snackBar.openFromComponent(component, {
            ...this.defaultPanelOptions(msgDuration),
            panelClass: 'nova-toast',
            ...options
        });
    }

    /**
     * Toast a set of errors returned by fluent validation
     * @param errors a key value set of errors. i.e. { 'email': 'Email is required', 'password': 'Password is required' }
     * @param prefix a prefix to add to the error message. Defaults to empty string. I.e. 'Login Error'
     */
    toastErrors(errors: any, prefix: string = '') {
        if (!errors || Object.keys(errors).length === 0) {
            return;
        }
        const errorMessages = [];
        Object.keys(errors).forEach((key) => {
            if (!this.isOAuthError(errors[key])) {
                errorMessages.push(errors[key]);
            }
        });
        this.toast(`${prefix}${prefix ? ':' : ''} ${errorMessages.join('\n')}`);
    }

    actionToast(
        message: string,
        action: string,
        options?: MatSnackBarConfig,
        msgDuration?: number
    ): MatSnackBarRef<SimpleSnackBar> {
        return this.snackBar.open(message, action, {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: msgDuration && msgDuration > 0 ? msgDuration : DEFAULT_MESSAGE_DURATION,
            ...options
        });
    }

    private defaultPanelOptions(msgDuration?: number): MatSnackBarConfig {
        return {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: msgDuration && msgDuration > 0 ? msgDuration : DEFAULT_MESSAGE_DURATION
        };
    }
}
